/** @typedef {import('../../types/client.types.js').ClientApiResponse} ClientApiResponse */

/** Shown when the request never reached the server (offline, DNS, CORS or a dropped connection). */
export const NETWORK_ERROR_MESSAGE = 'Cannot reach the server. Check your connection and try again.';

/**
 * The backend answers errors with a plain-text message. An empty body, or an HTML error page
 * from a proxy in front of the backend, is replaced by a short message naming the status.
 *
 * @param {number} status
 * @param {string} body
 * @returns {string}
 */
export function errorMessage(status, body) {
  const text = (body || '').trim();
  if (!text || /^<(!doctype|html)/i.test(text)) {
    return `The server answered with status ${status}.`;
  }
  return text;
}

const MONEY_KEYS = ['price', 'cost', 'amount'];
const CANONICAL_MONEY = /^\d{1,8}\.\d{2}$/;

/**
 * JSON text of a billing request, arrays holding requests of their own. Money fields hold canonical two-decimal strings (`12.50`) and are
 * written as JSON number literals, so the exact digits typed reach the backend without a floating
 * point round trip (`JSON.stringify` of the number 12.5 would drop the trailing zero).
 *
 * @param {Record<string, unknown>} request
 * @returns {string}
 */
export function billingBody(request) {
  const members = Object.entries(request)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return `${JSON.stringify(key)}:[${value.map(billingBody).join(',')}]`;
      }
      if (MONEY_KEYS.includes(key) && value !== null) {
        if (typeof value !== 'string' || !CANONICAL_MONEY.test(value)) {
          throw new TypeError(`${key} must be a canonical money string such as 12.50`);
        }
        return `${JSON.stringify(key)}:${value}`;
      }
      return `${JSON.stringify(key)}:${JSON.stringify(value)}`;
    });
  return `{${members.join(',')}}`;
}

export class ApiError extends Error {
  /** @param {number} status
   *  @param {string} message */
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

export class ClientApiAdapter {
  #baseUrl;
  #token;

  /** @param {string} baseUrl
   *  @param {string} token */
  constructor(baseUrl, token) {
    this.#baseUrl = baseUrl;
    this.#token = token;
  }

  /** @returns {Promise<ClientApiResponse[]>} */
  async getAllClients() {
    return this.#request('/clients');
  }

  /** @returns {Promise<ClientApiResponse>} */
  async getClient(slug) {
    return this.#request(`/clients/${slug}`);
  }

  /** @returns {Promise<ClientApiResponse>} */
  async createClient(request) {
    return this.#request('/clients', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /** @returns {Promise<ClientApiResponse>} */
  async updateClient(slug, request) {
    return this.#request(`/clients/${slug}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  /** @returns {Promise<void>} */
  async deleteClient(slug) {
    return this.#request(`/clients/${slug}`, { method: 'DELETE' });
  }

  /** @returns {Promise<ClientApiResponse>} */
  async addTemplate(slug, request) {
    return this.#request(`/clients/${slug}/templates`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /** @returns {Promise<import('../../types/client.types.js').AdminTemplateApiResponse>} */
  async getTemplate(slug, templateName) {
    return this.#request(`/clients/${slug}/templates/${templateName}`);
  }

  /** @returns {Promise<import('../../types/client.types.js').AdminTemplateApiResponse>} */
  async updateTemplate(slug, templateName, request) {
    return this.#request(`/clients/${slug}/templates/${templateName}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  /** @returns {Promise<import('../../types/client.types.js').UserApiResponse[]>} */
  async getUsers(clientSlug) {
    const query = clientSlug ? `?clientSlug=${encodeURIComponent(clientSlug)}` : '';
    return this.#request(`/users${query}`);
  }

  /** @returns {Promise<void>} */
  async deleteUser(id) {
    return this.#request(`/users/${id}`, { method: 'DELETE' });
  }

  /** @returns {Promise<void>} */
  async resetPassword(id, request) {
    return this.#request(`/users/${id}/password`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  /** @returns {Promise<ClientApiResponse>} */
  async removeTemplate(slug, templateName) {
    return this.#request(`/clients/${slug}/templates/${templateName}`, {
      method: 'DELETE',
    });
  }

  /**
   * Renders every published page of the client again with its current templates.
   *
   * @param {string} slug
   * @returns {Promise<import('../../types/client.types.js').RepublishApiResponse>}
   */
  async republishAll(slug) {
    return this.#request(`/clients/${slug}/republish`, {
      method: 'POST',
    });
  }

  /** @returns {Promise<import('../../types/billing.types.js').BillingItemApiResponse[]>} active items of live clients */
  async getBilling() {
    return this.#request('/billing');
  }

  /** @returns {Promise<import('../../types/billing.types.js').BillingSummaryApiResponse>} */
  async getBillingSummary() {
    return this.#request('/billing/summary');
  }

  /** @returns {Promise<import('../../types/billing.types.js').BillingReportApiResponse>} */
  async getBillingReport() {
    return this.#request('/billing/report');
  }

  /** @returns {Promise<import('../../types/billing.types.js').BillingItemApiResponse[]>} */
  async getClientBilling(slug) {
    return this.#request(`/clients/${slug}/billing`);
  }

  /** @param {import('../../types/billing.types.js').BillingItemApiRequest} request
   *  @returns {Promise<import('../../types/billing.types.js').BillingItemApiResponse>} */
  async createBillingItem(slug, request) {
    return this.#request(`/clients/${slug}/billing`, {
      method: 'POST',
      body: billingBody(request),
    });
  }

  /** @param {import('../../types/billing.types.js').BillingItemApiRequest} request
   *  @returns {Promise<import('../../types/billing.types.js').BillingItemApiResponse>} */
  async updateBillingItem(id, request) {
    return this.#request(`/billing/${id}`, {
      method: 'PUT',
      body: billingBody(request),
    });
  }

  /** @returns {Promise<void>} */
  async deleteBillingItem(id) {
    return this.#request(`/billing/${id}`, { method: 'DELETE' });
  }

  /** @returns {Promise<import('../../types/billing.types.js').BillingInvoiceApiResponse[]>} */
  async getBillingInvoices(id) {
    return this.#request(`/billing/${id}/invoices`);
  }

  /** @param {import('../../types/billing.types.js').MarkInvoicedApiRequest} request
   *  @returns {Promise<import('../../types/billing.types.js').BillingItemApiResponse>} */
  async markInvoiced(id, request) {
    return this.#request(`/billing/${id}/invoices`, {
      method: 'POST',
      body: billingBody(request),
    });
  }

  /** @param {import('../../types/billing.types.js').ClientInvoiceApiRequest} request
   *  @returns {Promise<import('../../types/billing.types.js').BillingItemApiResponse[]>} the invoiced items */
  async invoiceClient(slug, request) {
    return this.#request(`/clients/${slug}/billing/invoices`, {
      method: 'POST',
      body: billingBody(request),
    });
  }

  /** @returns {Promise<import('../../types/billing.types.js').BillingItemApiResponse>} */
  async undoInvoice(invoiceId) {
    return this.#request(`/billing/invoices/${invoiceId}`, { method: 'DELETE' });
  }

  /** @param {import('../../types/billing.types.js').InvoiceCostApiRequest} request
   *  @returns {Promise<import('../../types/billing.types.js').BillingInvoiceApiResponse>} */
  async correctInvoiceCost(invoiceId, request) {
    return this.#request(`/billing/invoices/${invoiceId}`, { method: 'PATCH', body: billingBody(request) });
  }

  /** @returns {Promise<import('../../types/billing.types.js').OneOffIncomeApiResponse[]>} newest first */
  async getClientOneOffIncomes(slug) {
    return this.#request(`/clients/${slug}/one-off-incomes`);
  }

  /** @param {import('../../types/billing.types.js').OneOffIncomeApiRequest} request
   *  @returns {Promise<import('../../types/billing.types.js').OneOffIncomeApiResponse>} */
  async createOneOffIncome(slug, request) {
    return this.#request(`/clients/${slug}/one-off-incomes`, { method: 'POST', body: billingBody(request) });
  }

  /** @param {import('../../types/billing.types.js').OneOffIncomeApiRequest} request
   *  @returns {Promise<import('../../types/billing.types.js').OneOffIncomeApiResponse>} */
  async updateOneOffIncome(id, request) {
    return this.#request(`/one-off-incomes/${id}`, { method: 'PUT', body: billingBody(request) });
  }

  /** @returns {Promise<void>} */
  async deleteOneOffIncome(id) {
    return this.#request(`/one-off-incomes/${id}`, { method: 'DELETE' });
  }

  /**
   * @param {{ from?: string, to?: string }} [range] yyyy-MM-dd bounds on the end of the cost's period,
   *   both inclusive and optional; without either the backend lists the last 12 months
   * @returns {Promise<import('../../types/billing.types.js').PlatformCostApiResponse[]>} newest period first
   */
  async getPlatformCosts({ from, to } = {}) {
    const query = new URLSearchParams();
    if (from) query.set('from', from);
    if (to) query.set('to', to);
    const text = query.toString();
    return this.#request(text ? `/platform-costs?${text}` : '/platform-costs');
  }

  /** @param {import('../../types/billing.types.js').PlatformCostApiRequest} request
   *  @returns {Promise<import('../../types/billing.types.js').PlatformCostApiResponse>} */
  async createPlatformCost(request) {
    return this.#request('/platform-costs', { method: 'POST', body: billingBody(request) });
  }

  /** @param {import('../../types/billing.types.js').PlatformCostApiRequest} request
   *  @returns {Promise<import('../../types/billing.types.js').PlatformCostApiResponse>} */
  async updatePlatformCost(id, request) {
    return this.#request(`/platform-costs/${id}`, { method: 'PUT', body: billingBody(request) });
  }

  /** @returns {Promise<void>} */
  async deletePlatformCost(id) {
    return this.#request(`/platform-costs/${id}`, { method: 'DELETE' });
  }

  async #request(path, options = {}) {
    let response;
    try {
      response = await fetch(`${this.#baseUrl}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.#token}`,
          ...options.headers,
        },
      });
    } catch {
      throw new ApiError(0, NETWORK_ERROR_MESSAGE);
    }

    if (!response.ok) {
      throw new ApiError(response.status, errorMessage(response.status, await response.text()));
    }

    if (response.status === 204) return undefined;
    return response.json();
  }
}
